import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { EmptyState } from "../components/EmptyState";
import { fadeUp, pop, stagger, tap } from "../motion";
import { IconResidents, IconSortAsc, IconSortDesc } from "../components/icons";
import { icpStatusLabel, RESIDENT_STATUSES, RESIDENT_STATUS_LABELS, RESIDENT_SUBSIDIES, RESIDENT_SUBSIDY_LABELS, residentStatusChipClass, useResidents, type ResidentItem, type ResidentStatus } from "../hooks/useResidents";
import { useAgenda } from "../hooks/useAgenda";
import { useHomes } from "../hooks/useSettings";
import { hkDateOf } from "../lib/dates";
import { Select } from "../components/Select";
import { ResidentQuickView } from "../components/ResidentQuickView";
import { GuidedIcpFlow } from "../components/GuidedIcpFlow";
import { AdmissionForm } from "../components/AdmissionForm";
function icpChipClass(status: ResidentItem["icpFillStatus"]): string {
  if (status === "filled") return "chip-ok";
  if (status === "form_available") return "chip-accent";
  return "chip-warn";
}
// Status filter dropdown options: "All" (the roster — excludes discharged;
// only the Discharged filter shows them) plus the other statuses. "new" is the
// admission state (no defined duration) so it is not offered as a filter —
// new residents appear under All / Active. "Active" keeps its current meaning:
// the working roster (new + active + hospital).
const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  ...RESIDENT_STATUSES.filter((st) => st !== "new").map((st) => ({ value: st, label: RESIDENT_STATUS_LABELS[st] })),
];

// Resident list sort: admit time / bed number / subsidy type, each with a
// direction toggle. Nulls (no admission date / no bed) always sort last.
const SORT_OPTIONS = [
  { value: "admission", label: "Admission date" },
  { value: "bed", label: "Bed" },
  { value: "subsidy", label: "Type" },
];
type SortKey = "admission" | "bed" | "subsidy";
function ResidentCard({ resident, showHome, showStatus }: { resident: ResidentItem; showHome: boolean; showStatus: boolean }) {
  const navigate = useNavigate();
  const { name, bed, homeName, subsidy, icpFillStatus, nextIcpDate, overdue, dueSoon, status, deepLink } = resident;
  const openQuickView = () => navigate(`/residents/${resident.id}`);
  return (
    <motion.li
      className="card resident-card card-interactive"
      variants={fadeUp}
      layout
      exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.2 } }}
      role="link"
      tabIndex={0}
      aria-label={`Quick view ${name}`}
      onClick={openQuickView}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openQuickView();
        }
      }}
    >
      <div className="resident-card-head">
        <div className="todo-main">
          <p className="todo-title">
            <span className="resident-name">
              {bed ? <span className="chip">{bed}</span> : null}
              <span>{name}</span>
            </span>
          </p>
        </div>
        <div className="resident-card-chips">
          {showHome ? <span className="chip">{homeName}</span> : null}
          <span className="chip">{RESIDENT_SUBSIDY_LABELS[subsidy]}</span>
        </div>
      </div>
      <div className="todo-meta">
        {showStatus && status !== "active" ? (
          <span className={`chip ${residentStatusChipClass(status)}`}>{RESIDENT_STATUS_LABELS[status as ResidentStatus] ?? status}</span>
        ) : null}
        {icpFillStatus ? (
          <span className={`chip ${icpChipClass(icpFillStatus)}`}>{icpStatusLabel(icpFillStatus)}</span>
        ) : null}
        {nextIcpDate ? <span className={`chip ${overdue ? "chip-danger" : dueSoon ? "chip-warn" : ""}`}>ICP {nextIcpDate.slice(0, 10)}</span> : null}
        {overdue ? <span className="chip chip-danger">overdue</span> : dueSoon ? <span className="chip chip-warn">due soon</span> : null}
      </div>
      {deepLink ? (
        <a className="btn btn-secondary resident-open" href={deepLink} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Open sheet</a>
      ) : null}
    </motion.li>
  );
}
export function ResidentsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [flowId, setFlowId] = useState<string | null>(null);
  const [admissionOpen, setAdmissionOpen] = useState(false);
  const { data: homes } = useHomes();
  const { data: agenda, isLoading: agendaLoading } = useAgenda();
  const [params, setParams] = useSearchParams();
  const qParam = params.get("q");
  const [homeId, setHomeId] = useState<string | null>(null);
  // Deep links from global search (/residents?q=name) span all homes — mark the
  // filter as touched from the start so the rota-home default never hides the hit.
  const [homeTouched, setHomeTouched] = useState(() => Boolean(qParam));
  const [status, setStatus] = useState<ResidentStatus | "all">("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("admission");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  // Deep link from global search: /residents?q=name pre-fills the search box.
  useEffect(() => {
    if (qParam) {
      setSearch(qParam);
      setParams({}, { replace: true });
    }
  }, [qParam, setParams]);
  // Default the home filter to the home of today's rota shift: the rota says
  // which home the OT works at each day, so Residents opens on that home's
  // residents. The user's own choice wins once they touch the filter; rest
  // days and missing rotas fall back to "All homes". The residents query waits
  // for the agenda (which carries today's shift) so the list never flashes
  // "all homes" before the default lands.
  const rotaHomeId = agenda?.shifts[0]?.homeId ?? null;
  const effectiveHomeId = homeTouched ? homeId : rotaHomeId;
  const { query, sync } = useResidents({
    homeId: effectiveHomeId,
    status: status === "all" ? "all" : status === "active" ? undefined : status,
    enabled: !agendaLoading,
  });
  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = query.data?.items ?? [];
    const filtered = q
      ? all.filter((r) => r.name.toLowerCase().includes(q) || (r.bed ?? "").toLowerCase().includes(q))
      : all;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sortKey === "bed") {
        if (!a.bed && b.bed) return 1; // nulls last, whichever direction
        if (a.bed && !b.bed) return -1;
        const c = (a.bed ?? "").localeCompare(b.bed ?? "", undefined, { numeric: true });
        return c === 0 ? a.name.localeCompare(b.name) * dir : c * dir;
      }
      if (sortKey === "subsidy") {
        const c = RESIDENT_SUBSIDIES.indexOf(a.subsidy) - RESIDENT_SUBSIDIES.indexOf(b.subsidy);
        return c === 0 ? a.name.localeCompare(b.name) * dir : c * dir;
      }
      // admit time (ISO dates compare lexicographically)
      if (!a.admissionDate && b.admissionDate) return 1; // nulls last
      if (a.admissionDate && !b.admissionDate) return -1;
      const c = (a.admissionDate ?? "").localeCompare(b.admissionDate ?? "");
      return c === 0 ? a.name.localeCompare(b.name) * dir : c * dir;
    });
  }, [query.data, search, sortKey, sortDir]);
  const bridgeHomes = (homes ?? []).filter((h) => h.hasBridge);
  const lastSyncedAt = (effectiveHomeId ? bridgeHomes.find((h) => h.id === effectiveHomeId) : bridgeHomes.sort((a, b) => (b.lastSyncedAt ?? "").localeCompare(a.lastSyncedAt ?? ""))[0])?.lastSyncedAt ?? null;
  const stale = lastSyncedAt ? Date.now() - new Date(lastSyncedAt).getTime() > 24 * 3600 * 1000 : true;
  // Auto-sync once when a bridged home has never been synced or is stale.
  const didAutoSync = useRef(false);
  useEffect(() => {
    if (didAutoSync.current || sync.isPending || bridgeHomes.length === 0) return;
    if (stale) {
      didAutoSync.current = true;
      sync.mutate(effectiveHomeId ?? undefined);
    }
  }, [bridgeHomes.length, effectiveHomeId, stale, sync.isPending]);
  const showHome = effectiveHomeId === null;
  const showStatus = status === "all";
  return (
    <>
      <motion.div className="page-head" variants={fadeUp} initial="hidden" animate="show">
        <h1>Residents</h1>
      </motion.div>
      <motion.div className="toolbar" variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.05 }}>
        <Select
          ariaLabel="Filter by home"
          value={effectiveHomeId ?? ""}
          onChange={(v) => { setHomeId(v === "" ? null : v); setHomeTouched(true); }}
          style={{ width: "auto" }}
          options={[{ value: "", label: "All homes" }, ...(homes ?? []).map((h) => ({ value: h.id, label: h.name }))]}
        />
        <Select
          ariaLabel="Filter by status"
          value={status}
          onChange={(v) => setStatus(v as ResidentStatus | "all")}
          style={{ width: "auto" }}
          options={STATUS_OPTIONS}
        />
        <input
          type="search"
          aria-label="Search residents"
          placeholder="Search name or bed…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 260 }}
        />
        <Select
          ariaLabel="Sort residents"
          value={sortKey}
          onChange={(v) => setSortKey(v as SortKey)}
          style={{ width: "auto" }}
          options={SORT_OPTIONS}
        />
        <button
          type="button"
          className="btn btn-secondary sort-dir"
          aria-label={`Sort ${sortDir === "asc" ? "ascending" : "descending"}`}
          title={sortDir === "asc" ? "Ascending" : "Descending"}
          onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
        >
          {sortDir === "asc" ? <IconSortAsc /> : <IconSortDesc />}
        </button>
        <div className="toolbar-actions">
          {bridgeHomes.length > 0 ? (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={sync.isPending}
              onClick={() => sync.mutate(effectiveHomeId ?? undefined)}
              style={{ minHeight: 40 }}
            >
              {sync.isPending ? "Syncing…" : "Sync from sheet"}
            </button>
          ) : null}
          <button type="button" className="btn btn-primary" onClick={() => setAdmissionOpen(true)}>+ New admission</button>
        </div>
      </motion.div>
      {bridgeHomes.length > 0 && lastSyncedAt ? (
        <p className="muted" style={{ marginTop: "var(--space-2)" }}>
          Last synced from the sheet: {hkDateOf(lastSyncedAt)}
        </p>
      ) : null}
      {(agendaLoading || query.isLoading) ? <p className="muted">Loading…</p> : null}
      {query.isError ? <p className="error">Failed to load residents. Retry.</p> : null}
      {!agendaLoading && !query.isLoading && !query.isError && items.length === 0 ? (
        <motion.div variants={pop} initial="hidden" animate="show">
          <EmptyState
            icon={<IconResidents />}
          title={effectiveHomeId ? "No residents in this home" : "No residents yet"}
          hint={search.trim() ? "No residents match your search." : undefined}
          action={
            bridgeHomes.length > 0 && !search.trim() ? (
              <motion.button type="button" className="btn btn-primary" {...tap} disabled={sync.isPending} onClick={() => sync.mutate(effectiveHomeId ?? undefined)}>
                {sync.isPending ? "Syncing…" : "Sync from sheet"}
              </motion.button>
            ) : undefined
          }
            />
          </motion.div>
      ) : null}
      <motion.ul className="grid-2 resident-list" variants={stagger(0.04)} initial="hidden" animate="show">
        <AnimatePresence initial={false}>
        {items.map((r) => (
          <ResidentCard key={r.id} resident={r} showHome={showHome} showStatus={showStatus} />
        ))}
        </AnimatePresence>
      </motion.ul>
      {flowId ? null : (
        <ResidentQuickView
          residentId={id ?? null}
          onClose={() => navigate("/residents")}
          onOpenFlow={() => { if (id) setFlowId(id); }}
        />
      )}
      <GuidedIcpFlow residentId={flowId} onClose={() => setFlowId(null)} />
      <AdmissionForm open={admissionOpen} defaultHomeId={effectiveHomeId ?? ""} onClose={() => setAdmissionOpen(false)} />
    </>
  );
}
